import { Grid, Paper } from '@mui/material';
import classNames from 'classnames';
import { BuyerProtectionModal } from 'components/BuyerProtectionModal/BuyerProtectionModal';
import { useCheckoutInteraction } from 'contexts/CheckoutInteractionContext';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import IosInformationCircleOutline from 'react-ionicons/lib/IosInformationCircleOutline';

import { YAGA_EXTERNALS } from '../../config/fe-routes';
import { useIsScreenSizeMax } from '../../utils/screen-size';
import { addElementsInHTMLString } from '../../utils/translation';
import A from '../Link/Link';
import Tooltip, { TooltipProps } from '../Tooltip/Tooltip';

type Props = {
  tooltip?: string;
  className?: string;
  tooltipProps?: TooltipProps;
  buyerSideFeesPercentage?: number;
  buyerSideFeesFixed?: number;
  buyerProtectionFeeEnabled?: boolean;
  align?: 'left' | 'center';
};

const PaymentShield = (props: Props) => {
  const { t } = useTranslation();
  const trackCheckoutInteraction = useCheckoutInteraction();

  const [isBuyerProtectionModalOpen, setBuyerProtectionModalOpen] = useState(false);

  // fires checkout_interaction only when this shared banner is rendered inside a checkout (the
  // context default is a no-op elsewhere), so buyer-protection info-modal opens are captured.
  // DEV-2120: the banner and summary buttons carry distinct values so each is identifiable by
  // value alone, like every other info_toggle.
  const handleBuyerProtectionModalOpenChange = (open: boolean) => {
    if (open) {
      trackCheckoutInteraction({
        element: 'info_toggle',
        action: 'tap',
        value: 'buyer_protection_banner',
        value_source: 'first_party',
      });
    }

    setBuyerProtectionModalOpen(open);
  };
  const shieldTooltip: Array<{ title: string; text: string }> = t('pages.buy.protected-tooltip', { returnObjects: true });

  const defaultTooltipContent = shieldTooltip
    .filter((entry) => entry.title)
    .map(
      (entry) => `
        <b>${entry.title}</b>
        <br />
        ${addElementsInHTMLString(entry.text, {
          'refund-link': (
            <A {...YAGA_EXTERNALS.REFUND()} rel="noopener noreferrer" target="_blank" className="primary">
              {entry['refund-link']}
            </A>
          ),
        })}
      `,
    )
    .join('<br /><br />');

  const {
    tooltip = defaultTooltipContent,
    className,
    tooltipProps,
    buyerProtectionFeeEnabled,
    buyerSideFeesPercentage,
    buyerSideFeesFixed,
    align = 'center',
  } = props;

  const message = buyerProtectionFeeEnabled
    ? t('components.page-product.product-details.shield.buyer-protection.full')
    : t('components.page-product.product-details.shield.default');

  const isMobileScreen = useIsScreenSizeMax('md');

  const tooltipComponent = buyerProtectionFeeEnabled ? (
    <BuyerProtectionModal
      isOpen={isBuyerProtectionModalOpen}
      setOpen={handleBuyerProtectionModalOpenChange}
      tooltipButtonClassName="p-0"
      buyerSideFeesPercentage={buyerSideFeesPercentage}
      buyerSideFeesFixed={buyerSideFeesFixed}
    />
  ) : (
    <Tooltip
      title={tooltip || 'components.page-product.product-details.shield-info'}
      iconClassName="info-icon"
      place="top"
      {...tooltipProps}
    >
      <IosInformationCircleOutline />
    </Tooltip>
  );

  return (
    <Grid className={classNames('shield', align, className)} container>
      <Paper elevation={0} className="shield-paper">
        <Grid container className="shield-content">
          <img src="/icons/icn-shield.svg" alt="" className="shield-icon" />

          <div className="shield-text">
            {!buyerProtectionFeeEnabled || !isMobileScreen ? (
              <>
                <span dangerouslySetInnerHTML={{ __html: message }} />
                &nbsp;
              </>
            ) : (
              <>
                <div className="shield-text-title">
                  {t('components.page-product.product-details.shield.buyer-protection.title')}&nbsp;
                  {tooltipComponent}
                </div>
                <div>
                  <span
                    dangerouslySetInnerHTML={{ __html: t('components.page-product.product-details.shield.buyer-protection.partial') }}
                  />
                  &nbsp;
                </div>
              </>
            )}
          </div>

          {(!buyerProtectionFeeEnabled || !isMobileScreen) && tooltipComponent}
        </Grid>
      </Paper>
    </Grid>
  );
};

export default PaymentShield;
